import { IField } from 'app/services/api5-service/api.interface';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { PhoneControlWithValue } from 'common/dynamic-form/utils/form-preparing/combined/phone/get-phone';
import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';

import { PhoneProps } from './model/phone-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';
import { FIELD_HIDDEN_CLASS } from '../../utils/class-fields';
import { getSelectProps } from '../select/select.utils';

/**
 * Clean phone value from formatting symbols. Map '+7 (950) 083-71-98' to '+79500837198'
 *
 * @param phone - phone string
 */
export function purifyPhoneFromMaskValue(phone: string): string {
  return phone.replace(/[ ()\-_]/g, '');
}

/**
 * Check, that phone string belongs to provided code
 * Example:
 * 1. phone '+7950332' belongs to code '+7'
 * 2. phone '+52' belongs to code '+528'
 * 3. phone '+10002223344' do not belongs to code '+900'
 *
 * @param phone - phone string
 * @param code - coutry code
 */
export function isPhoneBelongsToCode(phone: string, code: string): boolean {
  switch (true) {
    case !code:
      return false;
    case code.length > phone.length:
      return code.startsWith(phone);
    case code.length < phone.length:
      return phone.startsWith(code);
    default:
      return phone === code;
  }
}

/**
 * Append country code to phone
 *
 * @param phone - phone
 * @param code - country code
 */
export function appendCodeToPhone(phone: string, code: string): string {
  const purifiedValue = purifyPhoneFromMaskValue(phone);
  const valueWithoutCode = purifiedValue.replace('+', '');
  const valueWithOptionCode = `${code}${valueWithoutCode}`;
  return valueWithOptionCode;
}

/**
 * Replace phone code in string
 * Example:
 * 1. phone '+7 950', prev code '+7' next code '+682' will be replaced to '+682 950'
 * 3. phone '+7 950', prev code '+8' next code '+682' will be not changed - '+7 950'
 * 2. phone '+', prev code '+7' next code '+682' will be replaced to '+682'
 *
 * @param phone - phone
 * @param prevCode - prev phone code
 * @param nextCode - next phone code
 */
export function replacePhoneCodeInPhone(
  phone: string,
  prevCode: string,
  nextCode: string,
): string {
  // for case, when user input is '77', prev code '7', next code '77'. Do nothing
  if (phone === nextCode) {
    return phone;
  } else if (phone.startsWith(prevCode)) {
    return phone.replace(prevCode, nextCode);
  } else if (phone.startsWith(nextCode)) {
    return phone;
  } else {
    return appendCodeToPhone(phone, nextCode);
  }
}

/**
 * Get props for phone field
 *
 * @param control
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getPhoneProps(
  control: PhoneControlWithValue,
  field: IField,
  state: ISPFormState,
): PhoneProps {
  return {
    originalField: field,
    originalControl: control.control,
    fieldSettings$: of({
      displayMode: 'input-like',
      tagPlace: 'field',
    } as FieldSettings),
    selectCountry: control.select,
    selectCountryProps: getSelectProps(control.select, field, state),
    selectCountryDefaultValue: control.selectValue,
    isHidden: state.hiddenService.isHidden(
      control.control.$name,
      control.select.$name,
      field.$name,
    ),
    isHidden$: state.hiddenService.isHidden$(
      control.control.$name,
      control.select.$name,
      field.$name,
    ),
  };
}

/**
 * Get config for phone field
 *
 * @param control
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getPhoneConfig(
  control: PhoneControlWithValue,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Phone> {
  const props = getPhoneProps(control, field, state);

  return {
    key: control.control.$name,
    type: ISPFieldType.Phone,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
    props,
    expressions: {
      'props.isHidden': props.isHidden$,
      className: props.isHidden$?.pipe(
        map(isHidden => (isHidden ? FIELD_HIDDEN_CLASS : '')),
      ),
    },
  };
}
